import { createAppSlice } from '@exp/redux/createAppSlice';
import { DEFAULT_PRODUCT_CONFIGURATION } from '@exp/components/products/constants';
import mapAvailableOptionsFromProduct from '@exp/components/products/mapAvailableOptionsFromProduct';
import mapProductOptionsFromStores from '@exp/components/products/mapProductOptionsFromStores';
import structuredClone from '@exp/utilities/structuredClone';
import type CouponPropTypes from '@exp/components/products/couponPropTypes';
import updateConfigurationFromSelection from '@exp/components/products/updateConfigurationFromSelection';

import { EVENTS } from '../js/common/constants';
import { fetchCartSuggestionsByProduct } from '../cart/cartReducer';

import {
  addProductFavorite,
  claimProductCoupon,
  fetchProductCoupons,
  fetchProductFavorite,
  fetchProductInfo,
  fetchProductModules,
  fetchProductNotifications,
  fetchProductSuggestions,
  fetchRatingSummary,
  fetchStoreCampaigns,
  fetchStoreCoupons,
  removeProductFavorite,
  selectProductOption,
  setRatingSummary,
  setSelectedDeal,
  updateQuantity,
} from './productDetailsActions';

import * as API from '../js/common/api';
import { createBrandAvatarUrl } from '../js/common/utils';
import { sendEvent } from '../js/common/analytics';
import type ProductTypes from './productTypes';

type ProductDetailsState = {
  cartSuggestions?: ProductTypes.ProductCardData[],
  conversation?: ProductTypes.ProductConversation,
  errors?: ProductTypes.ErrorCodes[],
  fetchPending: boolean,
  lastUpdateTime?: number,
  modules: ProductTypes.Module[],
  productConfiguration: ProductTypes.ProductConfiguration,
  productCoupons?: CouponPropTypes.couponDataType,
  productDetails?: ProductTypes.Product,
  productFavorite?: ProductTypes.Favorite,
  productInfo?: ProductTypes.ProductDetail,
  productNotifications?: ProductTypes.ProductNotification[],
  quantity: number,
  ratingSummary?: ProductTypes.RatingSummary,
  selectedDeal?: ProductTypes.Store,
  storeCampaigns?: Record<string, ProductTypes.CampaignContent>,
  storeCoupons?: CouponPropTypes.couponType[],
  suggestions: ProductTypes.ProductCardData[],
  untargeted?: boolean,
};

const initialState: ProductDetailsState = {
  cartSuggestions: undefined,
  conversation: undefined,
  errors: undefined,
  fetchPending: false,
  lastUpdateTime: undefined,
  modules: [],
  productConfiguration: structuredClone(DEFAULT_PRODUCT_CONFIGURATION),
  productCoupons: undefined,
  productDetails: undefined,
  productFavorite: undefined,
  productInfo: undefined,
  productNotifications: undefined,
  quantity: 1,
  ratingSummary: undefined,
  selectedDeal: undefined,
  storeCampaigns: undefined,
  storeCoupons: undefined,
  suggestions: [],
  untargeted: undefined,
};

const productDetailsReducer = createAppSlice({
  name: 'productDetails',
  initialState,
  reducers: (create) => ({
    continueProductConversation: create.asyncThunk(async (request: { conversationId: string, message: string }) => {
      const res = await API.continueProductConversation(request);
      return res.data;
    }, {
      fulfilled: (state, action) => {
        state.conversation = action.payload;
      },
    }),
    fetchProductConversationSuggestions: create.asyncThunk(async (request: { orgId: number, productCode: string }) => {
      const res = await API.fetchProductConversationSuggestions(request);
      return res.data;
    }),
    startProductConversation: create.asyncThunk(async (request: {
      message: string,
      orgId: number,
      productCode: string,
    }) => {
      const res = await API.startProductConversation(request);
      return res.data;
    }, {
      fulfilled: (state, action) => {
        state.conversation = action.payload;
      },
    }),
    submitProductConversationFeedback: create.asyncThunk(async (request: {
      accepted: boolean,
      conversationId: string,
    }) => {
      const res = await API.submitProductConversationFeedback(request);
      return res.data;
    }),
  }),
  extraReducers: (builder) => {
    builder.addCase(addProductFavorite.fulfilled, (state, action) => {
      state.productFavorite = action.payload.data;
    });

    builder.addCase(claimProductCoupon.rejected, (state, action) => {
      const { meta: { arg: { couponId } } } = action;

      state.productCoupons = {
        ...state.productCoupons,
        claimable: state
          .productCoupons
          .claimable
          .map((coupon) => (coupon.id === couponId ? { ...coupon, claimed: false } : coupon)),
      };
    });

    builder.addCase(
      fetchCartSuggestionsByProduct.fulfilled,
      (state, action) => {
        state.cartSuggestions = action.payload.data?.resultItems as ProductTypes.ProductCardData[];
      },
    );

    builder.addCase(fetchProductCoupons.fulfilled, (state, action) => {
      state.productCoupons = action.payload.data;
    });

    builder.addCase(fetchProductFavorite.fulfilled, (state, action) => {
      state.productFavorite = action.payload.data || null;
    });

    builder.addCase(fetchProductInfo.fulfilled, (state, action) => {
      const { data } = action.payload;

      state.fetchPending = false;
      state.lastUpdateTime = Date.now();
      state.productInfo = data;

      if (state.productInfo.organization) {
        const { organization } = state.productInfo;

        state.productInfo.organization.avatar =
          createBrandAvatarUrl(organization.avatarKey, organization.modifiedTime);
      }

      if (data.errorCodes) {
        state.productInfo = undefined;
        state.errors = data.errorCodes;
        state.untargeted = true;
      } else if (data.stores) {
        const { stores } = data;

        const {
          allOptions,
          allSkus,
          misconfiguredValue,
        } = mapProductOptionsFromStores(stores);

        if (misconfiguredValue) {
          const {
            organization: {
              id: orgId,
              name: orgName,
            },
            stores: {
              0: {
                catalogProduct: {
                  name: productName,
                  productCode,
                },
              },
            },
          } = data;

          // send a misconfiguration event so we can track this
          sendEvent(EVENTS.STORE_PRODUCT_DETAIL_MISCONFIGURED_PRODUCT, {
            ...misconfiguredValue,
            mfgId: orgId,
            orgId,
            orgName,
            productCode,
            productName,
          });
        }

        state.productConfiguration.allOptions = allOptions;
        state.productConfiguration.allSkus = allSkus;
        state.productConfiguration.shippingAddress = data.shippingAddress;
      }
    });

    builder.addCase(fetchProductInfo.pending, (state) => {
      state.fetchPending = true;
    });

    builder.addCase(fetchProductInfo.rejected, (state) => {
      state.fetchPending = false;
      state.lastUpdateTime = Date.now();
      state.untargeted = true;
    });

    builder.addCase(fetchProductModules.fulfilled, (state, action) => {
      state.modules = action.payload.data;
    });

    builder.addCase(fetchProductNotifications.fulfilled, (state, action) => {
      state.fetchPending = false;
      state.lastUpdateTime = Date.now();
      state.productNotifications = [].concat(action.payload.data);
    });

    builder.addCase(fetchProductNotifications.pending, (state) => {
      state.fetchPending = true;
    });

    builder.addCase(fetchProductNotifications.rejected, (state) => {
      state.fetchPending = false;
      state.lastUpdateTime = null;
    });

    builder.addCase(fetchStoreCampaigns.fulfilled, (state, action) => {
      state.storeCampaigns = action.payload.data;
      state.untargeted = Object.values(action.payload.data || {}).length === 0;
    });

    builder.addCase(fetchStoreCoupons.fulfilled, (state, action) => {
      state.storeCoupons = action.payload.data?.content;
    });

    builder.addCase(fetchRatingSummary.fulfilled, (state, action) => {
      state.ratingSummary = action.payload.data;
    });

    builder.addCase(fetchRatingSummary.pending, (state) => {
      state.ratingSummary = { pending: true };
    });

    builder.addCase(fetchRatingSummary.rejected, (state) => {
      state.ratingSummary = {};
    });

    builder.addCase(fetchProductSuggestions.fulfilled, (state, action) => {
      state.suggestions = action.payload.data.resultItems;
    });

    builder.addCase(removeProductFavorite.fulfilled, (state) => {
      state.productFavorite = null;
    });

    builder.addCase(selectProductOption, (state, action) => {
      const { payload: selectedOption } = action;

      state.productConfiguration = updateConfigurationFromSelection(
        state.productDetails,
        state.productConfiguration,
        selectedOption,
      );
    });

    builder.addCase(setRatingSummary, (state, action) => {
      state.ratingSummary = action.payload;
    });

    builder.addCase(setSelectedDeal, (state, action) => {
      const selectedDeal = action.payload;
      const catalogProduct = selectedDeal?.catalogProduct;

      state.selectedDeal = selectedDeal;

      if (catalogProduct) {
        state.productDetails = catalogProduct;
        state.productConfiguration =
          mapAvailableOptionsFromProduct(selectedDeal, state.productConfiguration);
      }
    });

    builder.addCase(updateQuantity, (state, action) => {
      state.quantity = action.payload;
    });
  },
});

export const {
  continueProductConversation,
  fetchProductConversationSuggestions,
  startProductConversation,
  submitProductConversationFeedback,
} = productDetailsReducer.actions;

export default productDetailsReducer.reducer;
